import jsPDF from "jspdf";

/**
 * Render a tailored resume (already structured) into a downloadable PDF blob/file.
 * Mirrors the layout used by Dashboard.jsx so bulk + single downloads stay consistent.
 *
 * @param {object} analysis - The full analysis doc returned by GET /analyses/{id}
 * @param {object} [opts]
 * @param {string} [opts.filename] - Override the auto-derived filename.
 * @param {boolean} [opts.save=true] - When true, triggers a browser download.
 * @returns {{ doc: jsPDF, filename: string }}
 */
export function buildTailoredResumePdf(analysis, opts = {}) {
  const tailored = (analysis?.result || {}).tailored_resume || {};
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 50;
  const maxWidth = pageWidth - margin * 2;
  let y = margin;

  const writeLine = (text, lineOpts = {}) => {
    const fontSize = lineOpts.size || 11;
    const bold = lineOpts.bold || false;
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(fontSize);
    const lines = doc.splitTextToSize(text || "", maxWidth);
    lines.forEach((ln) => {
      if (y > 760) { doc.addPage(); y = margin; }
      doc.text(ln, margin, y);
      y += fontSize * 1.3;
    });
  };

  writeLine(
    (tailored.headline || analysis?.job_title || "Tailored Resume").toUpperCase(),
    { size: 18, bold: true },
  );
  y += 6;

  if (tailored.summary) {
    writeLine("SUMMARY", { size: 11, bold: true });
    writeLine(tailored.summary);
    y += 6;
  }
  if ((tailored.key_skills || []).length) {
    writeLine("KEY SKILLS", { size: 11, bold: true });
    writeLine((tailored.key_skills || []).join(" · "));
    y += 6;
  }
  if ((tailored.experience || []).length) {
    writeLine("EXPERIENCE", { size: 11, bold: true });
    tailored.experience.forEach((exp) => {
      writeLine(
        `${exp.title || ""} — ${exp.company || ""}  (${exp.dates || ""})`,
        { bold: true },
      );
      (exp.bullets || []).forEach((b) => writeLine(`• ${b}`));
      y += 4;
    });
  }
  if ((tailored.education || []).length) {
    writeLine("EDUCATION", { size: 11, bold: true });
    tailored.education.forEach((ed) =>
      writeLine(`${ed.degree || ""} — ${ed.school || ""} (${ed.dates || ""})`),
    );
    y += 6;
  }
  if ((tailored.additional || []).length) {
    writeLine("ADDITIONAL", { size: 11, bold: true });
    tailored.additional.forEach((a) => writeLine(`• ${a}`));
  }

  const safeTitle = (analysis?.job_title || "tailored-resume").replace(/[^a-z0-9]+/gi, "-");
  const safeCompany = (analysis?.company_name || "").replace(/[^a-z0-9]+/gi, "-");
  const filename =
    opts.filename ||
    (safeCompany
      ? `${safeCompany}-${safeTitle}.pdf`.toLowerCase()
      : `${safeTitle}.pdf`.toLowerCase());

  if (opts.save !== false) doc.save(filename);
  return { doc, filename };
}
